import { TMSEquipmentType } from 'types/Load';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';

import {
  AppointmentSuggestionPolicy,
  RevenovaTenant,
  tenantAppointmentSuggestionPolicies,
} from './constants';
import { getTenantFromString } from './helpers';

export function getAppointmentSuggestionPolicy(
  tenant: MaybeUndef<string>
): AppointmentSuggestionPolicy {
  const detectedTenant = getTenantFromString(tenant ?? '');
  return (
    (detectedTenant && tenantAppointmentSuggestionPolicies[detectedTenant]) ??
    AppointmentSuggestionPolicy.Legacy
  );
}

interface AppointmentSuggestionInput {
  tenant: MaybeUndef<string>;
  apptStartTime: unknown;
  apptEndTime: unknown;
  fallbackTime: unknown;
  externalTMSID?: Maybe<string>;
  apptRequired?: Maybe<boolean>;
}

interface AppointmentSuggestion {
  apptStartTime: unknown;
  apptEndTime: unknown;
}

export function resolveAppointmentSuggestion(
  input: AppointmentSuggestionInput
): AppointmentSuggestion {
  const policy = getAppointmentSuggestionPolicy(input.tenant);
  const result: AppointmentSuggestion = {
    apptStartTime: '',
    apptEndTime: '',
  };
  if (
    policy === AppointmentSuggestionPolicy.Suppress ||
    (policy === AppointmentSuggestionPolicy.ExplicitOnly &&
      (!input.externalTMSID?.trim() || input.apptRequired !== true))
  ) {
    return result;
  }
  const hasStart =
    !!input.apptStartTime && String(input.apptStartTime).trim() !== '';
  if (hasStart) {
    result.apptStartTime = input.apptStartTime;
  } else if (policy === AppointmentSuggestionPolicy.Legacy) {
    result.apptStartTime = input.fallbackTime;
  }
  result.apptEndTime = input.apptEndTime;
  return result;
}

/**
 * Checks if a transportType string looks like a Salesforce ID.
 * Salesforce IDs are typically 15 or 18 alphanumeric characters.
 *
 * @param transportType - The transport type string to check
 * @returns true if the string looks like a Salesforce ID
 */
function looksLikeSalesforceId(transportType: string): boolean {
  // Salesforce IDs are 15 or 18 alphanumeric characters, no spaces or special chars
  // More conservative: if it's 15+ chars, alphanumeric only, and doesn't contain common words
  return (
    transportType.length >= 15 &&
    /^[a-zA-Z0-9]+$/.test(transportType) &&
    !transportType.toLowerCase().includes('reefer') &&
    !transportType.toLowerCase().includes('refrigerat') &&
    !transportType.toLowerCase().includes('flatbed') &&
    !transportType.toLowerCase().includes('van')
  );
}

/**
 * Van/Reefer records (e.g. Great Lakes' "53 Van/Reefer") book a trailer that can run dry or
 * cold. Picking one doesn't make a load refrigerated: the Mode decides that.
 */
export function isVanOrReeferEquipment(name: MaybeUndef<string>): boolean {
  if (!name) return false;
  const normalized = name.toLowerCase();
  return /\bvan\b/.test(normalized) && /\breefer\b/.test(normalized);
}

/** Matches the backend's TemperatureControlled check on the Revenova mode name. */
function isRefrigeratedMode(mode: MaybeUndef<string>): boolean {
  const normalized = mode?.toLowerCase() ?? '';
  return (
    normalized.includes('reefer') ||
    normalized.includes('refrigerat') ||
    normalized.includes('rltl')
  );
}

/**
 * Checks if an equipment type is refrigerated by examining the transport type.
 * Looks up the equipment type name from equipmentTypes if available, otherwise
 * checks the transportType string directly.
 *
 * @param transportType - The transport type ID or name string
 * @param equipmentTypes - Optional array of equipment types to look up the name from
 * @param mode - The load's Mode; decides Van/Reefer equipment, which can run either way
 * @returns true if the equipment is refrigerated (contains 'reefer', 'refrigerat', or 'rltl'),
 *          or null if we cannot determine (equipmentTypes undefined and transportType looks like an ID)
 */
export function isEquipmentRefrigerated(
  transportType: MaybeUndef<string>,
  equipmentTypes: Undef<TMSEquipmentType[]>,
  mode?: MaybeUndef<string>
): Maybe<boolean> {
  if (!transportType) return false;

  // Get equipment type name (look up in equipmentTypes if available)
  let equipmentTypeName: string;
  if (equipmentTypes) {
    const equipmentType = equipmentTypes.find(
      (et: TMSEquipmentType) => et.id === transportType
    );
    if (equipmentType) {
      // Found the equipment type - use its name
      equipmentTypeName = equipmentType.name.toLowerCase();
    } else {
      // Equipment not found in list - could be stale/incomplete data or race condition
      // If transportType looks like a Salesforce ID, we cannot reliably determine if it's refrigerated
      if (looksLikeSalesforceId(transportType)) {
        return null; // Cannot determine - equipment not found and transportType is an ID
      }
      // Fall back to checking transportType string directly (might be a name)
      equipmentTypeName = transportType.toLowerCase();
    }
  } else {
    // If equipmentTypes is undefined, we can only check the transportType string directly
    // If it looks like a Salesforce ID, we cannot reliably determine if it's refrigerated
    if (looksLikeSalesforceId(transportType)) {
      return null; // Cannot determine - need equipmentTypes to look up the name
    }
    equipmentTypeName = transportType.toLowerCase();
  }

  if (isVanOrReeferEquipment(equipmentTypeName)) {
    return isRefrigeratedMode(mode);
  }

  // Check if equipment is refrigerated
  return (
    equipmentTypeName.includes('rltl') ||
    equipmentTypeName.includes('reefer') ||
    equipmentTypeName.includes('refrigerat')
  );
}

/**
 * Derives the load Mode from a transport/equipment type string.
 * Matches the backend logic in drumkit/common/integrations/tms/revenova/load.go
 *
 * @param transportType - The transport type string (e.g., "Reefer", "Reefer 53'", "Flatbed Trailer")
 * @returns The derived mode string, or null if no specific mode mapping exists
 */
export function deriveModeFromTransportType(
  transportType: MaybeUndef<string>,
  tenant: string
): Maybe<string> {
  if (!transportType) return null;

  const normalized = transportType.toLowerCase();
  const detectedTenant = getTenantFromString(tenant);
  const isProsponsiveTenant = detectedTenant === RevenovaTenant.Prosponsive;
  const isCoolerTenant = detectedTenant === RevenovaTenant.Cooler;
  const isGreatLakesTenant = detectedTenant === RevenovaTenant.GreatLakes;
  if (normalized.includes('rltl') || normalized.includes('refrigerated ltl')) {
    if (isCoolerTenant) {
      return 'RLTL';
    }
    // RLTL is a Cooler-specific mode; fallback to LTL for other tenants.
    return 'LTL';
  }
  // Van/Reefer is booked as a Truckload; it falls through to the default below.
  if (
    !isVanOrReeferEquipment(normalized) &&
    (normalized.includes('reefer') || normalized.includes('refrigerat'))
  ) {
    if (isProsponsiveTenant) {
      return 'TL-Refrigerated';
    }
    if (isCoolerTenant || isGreatLakesTenant) {
      return 'Reefer';
    }
    return 'Refrigerated';
  }
  if (normalized.includes('flatbed')) {
    if (isProsponsiveTenant) {
      return 'TL-Flatbed';
    }
    return 'Flatbed';
  }
  if (normalized.includes('dry van') || normalized.includes('dryvan')) {
    if (isProsponsiveTenant) {
      return 'TL-Dry Van';
    }
    // Dry Van is equipment on Cooler/GL; mode is Truckload.
    if (isCoolerTenant || isGreatLakesTenant) {
      return 'Truckload';
    }
    return 'Dry Van';
  }
  if (
    normalized.includes('ltl') ||
    normalized.includes('less than truckload')
  ) {
    return 'LTL';
  }

  // Van, Container, and others don't have a specific mode mapping
  if (isProsponsiveTenant) {
    return 'TL-Dry Van';
  }
  if (isCoolerTenant || isGreatLakesTenant) {
    return 'Truckload';
  }
  return 'TL';
}
